﻿import OnMemoryImageCache from "./cache/onMemoryImageCache";
import { ICloudStorageAccess } from "./cloudstorage/cloudStorageAccess";
import { FirebaseAccess } from "./cloudstorage/firebase";
import { LocalFileAccess } from "./cloudstorage/local";
import { PreviewDaishi, TagID, TrWL } from "./entities";
import {
  FrameLevelCellid,
  IllegalArgumentsError,
  ImageChunk,
  Point,
  Rectangle,
} from "./util";
import { View } from "./view";

export default class Controller {
  /**
   * 拡大率のピッチ数
   * 値を小さくすると滑らかな拡大になる
   */
  private readonly MAGNIFICATION_PITCH: number = 500;
  /**
   * キャンバス要素
   */
  private readonly canvas: HTMLCanvasElement;
  /**
   * 出力先のキャンバス全体
   */
  private readonly canvasRect: Rectangle;
  /**
   * クラウドストレージへのアクセス
   */
  private readonly repository: ICloudStorageAccess;

  private readonly view: View;

  private state: {
    tag: TagID;
    daishiMeta: PreviewDaishi;
    currentRect: Rectangle;
    currentLevel: number;
  } = {
    tag: {} as any,
    daishiMeta: {} as any,
    currentRect: {} as any,
    currentLevel: 0,
  };

  /**
   * 現在、ドラッグ中か
   */
  private _dragging: boolean = false;
  /**
   * ドラッグが開始された地点
   */
  private _dragStartAt: Point;
  /**
   * ドラッグが開始された地点からの距地
   */
  private _dragDiff: Point;
  /**
   * ドラッグが終了した地点
   */
  private _dragEndAt: Point;

  /**
   * @param container アプリケーションコンテナ
   */
  constructor(container: HTMLElement | null, repository: ICloudStorageAccess) {
    if (container == null) throw new Error("cant find container");

    /** 画像のレンダリング先となる キャンバス要素 */
    this.canvas = document.createElement("canvas");

    // this.canvas.width = 886;
    // this.canvas.height = 564;
    this.canvas.width = container.clientWidth;
    this.canvas.height = container.clientHeight;

    container.appendChild(this.canvas);

    this.canvasRect = new Rectangle(
      new Point(this.canvas.clientLeft, this.canvas.clientTop),
      this.canvas.width,
      this.canvas.height,
    );

    /** クラウドストレージへのアクセスオブジェクト */
    this.repository = repository;

    /** キャンバスへの描画用コンテキスト */
    const context = this.canvas.getContext("2d");
    if (context == null) {
      throw new IllegalArgumentsError("Cant find context of canvas");
    }

    /** キャッシュ定義 */
    const cache = new OnMemoryImageCache();

    this.view = new View(context, cache);
    console.debug("Controllerの初期化 完了");
  }

  /**
   * 初期化する
   * @param tag 初期表示対象のプレビュー台紙のタグ
   */
  public async init(tag: TagID) {
    // キャンバス要素に描画されている内容をクリア
    this.view.clearScreen(this.canvasRect);

    // キャンバス要素 にハンドラを登録する
    this.registerHandlers();

    this.state.tag = tag;

    return this.repository
      .loadPreviewDaishi(tag)
      .then(
        // プレビュー台紙から、初期表示対象のフレーム一覧を取得する。
        (previewDaishi: PreviewDaishi) => {
          this.state.daishiMeta = previewDaishi;
          this.state.currentRect = new Rectangle(
            0,
            0,
            previewDaishi.size.width,
            previewDaishi.size.height,
          );
          return previewDaishi.initialFrameList(this.canvasRect);
        },
      )
      .then(
        // フレーム一覧をもとに、レンダリング対象の画像情報を取得する。
        (result: [Rectangle, FrameLevelCellid[]]) => {
          this.state.currentRect = result[0];
          return result[1].map((frame) =>
            this.repository.resolveUrl(tag, frame),
          );
        },
      )
      .then(
        // レンダリング対象の画像一覧を View に引き渡して出力する
        async (imageChunks) => {
          const images = await Promise.all(imageChunks);
          return this.view.showImages(images);
        },
      );
  }

  /**
   * DOMイベントに対するハンドラを登録する
   * @todo: touch 系イベントにも対応する。単純に 'pointermove' 等を採用するのではダメそう
   */
  private registerHandlers() {
    // マウスイベント
    this.canvas.addEventListener(
      "mousedown",
      (ev: PointerEvent) => this.mouseDown(ev),
      { passive: false },
    );
    this.canvas.addEventListener(
      "mousemove",
      (ev: PointerEvent) => this.mouseMove(ev),
      { passive: false },
    );
    this.canvas.addEventListener(
      "mouseup",
      (ev: PointerEvent) => this.mouseUp(ev),
      { passive: false },
    );
    this.canvas.addEventListener(
      "wheel",
      (ev: WheelEvent) => this.onWheelHandler(ev),
      { passive: false },
    );
    this.canvas.addEventListener(
      "touchstart",
      (ev: TouchEvent) => this.touchStart(ev),
      { passive: false },
    );
    this.canvas.addEventListener(
      "touchmove",
      (ev: TouchEvent) => this.touchMove(ev),
      { passive: false },
    );
    this.canvas.addEventListener(
      "touchend",
      (ev: TouchEvent) => this.touchEnd(ev),
      { passive: false },
    );
  }

  // ***** handler

  private mouseDown(ev: PointerEvent) {
    //OnLButtonDown
    ev.preventDefault();
    const x = ev.clientX,
      y = ev.clientY;
    console.debug(`mouseDown:{x:${x}, y:${y}}`);
    this.dragStart(x, y);
  } //mouseDown

  private mouseMove(ev: MouseEvent) {
    //OnMouseMove
    if (!this._dragging) return;
    ev.preventDefault();
    const x = ev.clientX,
      y = ev.clientY;
    console.debug(`mouseMove:{x:${x}, y:${y}}`);
    this.dragMove(x, y);
  } //mouseMove

  private mouseUp(ev: MouseEvent) {
    //dragEnd
    ev.preventDefault();
    console.debug(
      `mouseUp: ${this._dragDiff} x:${ev.clientX}, y:${ev.clientY}`,
    );
    this.dragEnd(ev.clientX, ev.clientY);
  } //mouseUp

  private sp0 = new Point(0, 0);
  private sp1 = new Point(0, 0);
  private zoomP = new Point(0, 0);
  private tLast = new Rectangle(new Point(0, 0), 0, 0);
  private fLast = new Rectangle(new Point(0, 0), 0, 0);
  private touchMoveCnt = 0;

  private touchStart(ev: TouchEvent) {
    ev.preventDefault();
    console.log(`TouchStart ${ev.targetTouches.length}  *********************`);
    if (ev.targetTouches.length == 1) {
      //Drag Start
      let touch = ev.targetTouches[0];
      let x = ev.targetTouches[0].clientX;
      let y = ev.targetTouches[0].clientY;
      //console.log(`Pinch (${~~x}, ${~~y}))`);
      this.dragStart(touch.clientX, touch.clientY);
    } else if (ev.targetTouches.length == 2) {
      //Pinch Zoom
      if (this._dragging) {
        this.dragCancel();
        return;
      }
      this.touchMoveCnt = 0;

      this.tLast = this.state.daishiMeta.getTlast();
      this.fLast = this.state.daishiMeta.getFlast();
      let touch0 = ev.targetTouches[0];
      let touch1 = ev.targetTouches[1];
      this.sp0 = new Point(touch0.clientX, touch0.clientY);
      this.sp1 = new Point(touch1.clientX, touch1.clientY);
      this.zoomP = new Point(
        (this.sp0.x + this.sp1.x) / 2,
        (this.sp0.y + this.sp1.y) / 2,
      );
    } else {
      //notImplement
      console.error("touchStart まだ実装されていない");
    }
  } //touchStart

  private touchMove(ev: TouchEvent) {
    ev.preventDefault();
    console.log(`TouchMove ${ev.targetTouches.length}`);
    if (ev.targetTouches.length == 1) {
      //Drag Move
      if (!this._dragging) return;
      let touch = ev.targetTouches[0];
      this.dragMove(touch.clientX, touch.clientY);
    } else if (ev.targetTouches.length == 2) {
      //Pinch Zoom
      if (++this.touchMoveCnt < 10) return;
      this.touchMoveCnt = 0;

      let touch0 = ev.targetTouches[0];
      let touch1 = ev.targetTouches[1];
      let mp0 = new Point(touch0.clientX, touch0.clientY);
      let mp1 = new Point(touch1.clientX, touch1.clientY);
      const io = "i";
      let dsp = this.sp0.distance(this.sp1); //sp0--sp1
      let dmp = mp0.distance(mp1); //mp0--mp1
      let ratio = dmp / dsp;
      //ratio = (mP0-sp0)+(mp1-sp1)+(sp1-sp0) / (sp1-sp2)
      //let  x= Math.abs(mp0.x - this.sp0.x)+Math.abs(mp1.x - this.sp1.x);
      //let  y= Math.abs(mp0.y - this.sp0.y)+Math.abs(mp1.y - this.sp1.y);
      //let  sp12= Math.abs(this.sp1.x-this.sp0.x)+Math.abs(this.sp1.y-this.sp0.y);
      //let  ratio= (x+y+sp12) / sp12;
      let tLast = this.tLast;
      let fLast = this.fLast;
      this.zooming(io, this.zoomP, ratio, tLast, fLast);
    } else {
      //notImplement
      console.error(`touchMove まだ実装されていない`);
    }
  } //touchMove

  private touchEnd(ev: TouchEvent) {
    //0.ev.preventDefault();
    console.log(`touchEnd ${ev.targetTouches.length}  *********************`);
    if (ev.targetTouches.length == 0) {
      //Drag Move
      if (!this._dragging) return;
      this.dragEnd(0, 0);
    } else if (ev.targetTouches.length == 1) {
      //Pinch Zoom
    } else {
      //notImplement
      console.error("touchEnd >=2 まだ実装されていない");
    }
  } //touchEnd

  // ホイール操作イベントのハンドラ関数
  private onWheelHandler(ev: WheelEvent): void {
    ev.preventDefault();
    const io = ev.deltaY < 0 ? "i" : "o"; //zoomIn zoomOut
    let point = new Point(ev.clientX, ev.clientY);
    console.debug(
      `onWheelHandler(io:${io}):{x:${ev.clientX}, y:${ev.clientY}}`,
    );
    const POINT_ZOOM_RATIO = 1.5;

    let tLast = this.state.daishiMeta.getTlast();
    let fLast = this.state.daishiMeta.getFlast();
    this.zooming(io, point, POINT_ZOOM_RATIO, tLast, fLast);
  } //onWheelHandler

  //  ***** handler
  //  ************************
  //  ***** servise routine

  private dragStart(x: number, y: number) {
    //OnLButtonDown
    console.debug(`dragStart:{x:${~~x}, y:${~~y}}`);
    this._dragStartAt = new Point(x, y);
    this._dragging = true;
  } //dragStart

  private dragMove(xx: number, yy: number) {
    //OnMouseMove
    console.debug(`dragMove: _dragDiff:[${this._dragDiff}] x:${xx}, y:${yy}`);
    const x = xx - this._dragStartAt.x,
      y = yy - this._dragStartAt.y;
    if (x == 0 && y == 0) return;
    //if(!this.canvasRect.ptInRect(new Point(x,y)))  return;
    this._dragDiff = new Point(x, y);
    this._dragStartAt = new Point(xx, yy);
    this.scrollTo(this._dragDiff);
  } //dragMove

  private dragEnd(xx: number, yy: number) {
    //dragEnd
    console.debug(`dragEnd: ${this._dragDiff} x:${xx}, y:${yy}`);
    if (xx > 0 || yy > 0) {
      const x = xx - this._dragStartAt.x,
        y = yy - this._dragStartAt.y;
      this._dragDiff = new Point(x, y);
      this.fixScrollTo(this._dragDiff);
    }
    this._dragging = false;
  } //dragEnd

  private dragCancel() {
    //dragEnd
    console.debug(`dragCancel:`);
    this._dragging = false;
  } //dragCancel

  private zooming(
    io: string,
    point: Point,
    ratio: number,
    tLast: Rectangle,
    fLast: Rectangle,
  ): void {
    //本来はview.zoom()に実装すべき????,ここに実装する
    console.log(`zooming(io:${io} point:${point} ratio:${ratio})`);
    console.log(` tLast:${tLast} fLast:${fLast}`);

    if (tLast.isRectEmpty()) {
      return;
    }
    // **(1)*zoomPoint***************************
    //let  POINT_ZOOM_RATIO= 1.5;
    //let  POINT_ZOOM_RATIO= 2.0;    //19.11.12
    let rects = this.zoomPoint(io, ratio, point, tLast, fLast);
    let toRnew = rects[0];
    let fromRnew = rects[1];

    let sz = this.state.daishiMeta.size;
    let splitCnt = this.state.daishiMeta.splitCnt; //19.11.8
    let szDB = new Rectangle(
      new Point(0, 0),
      sz.cx * splitCnt.x,
      sz.cy * splitCnt.y,
    );

    //this.expandSet(this.canvasRect,szDB,rFNew,rTNew);
    {
      //border一杯に拡大後set
      let tBorder = this.canvasRect;
      let fBorder = szDB;
      // **(2)*expand*********************
      let frames = this.expand(toRnew, fromRnew, tBorder, fBorder);
      let toRRNew = frames[0];
      let fromRRNew = frames[1];
      if (!this.checkRange(toRRNew, fromRRNew)) {
        this.state.daishiMeta.setTlast(toRRNew);
        this.state.daishiMeta.setFlast(fromRRNew);
      }
      this.refresh(toRRNew, fromRRNew);
    }
    console.debug("on wheel end.");
  } //zooming

  /**
   * ドラッグ途中イベントのハンドラ関数
   * @param ev
   */

  public scrollTo(dragDiff: Point): void {
    //前処理
    let tLast = this.state.daishiMeta.getTlast();
    let fLast = this.state.daishiMeta.getFlast();

    //scrollPoint
    let left = tLast.left + dragDiff.x;
    let top = tLast.top + dragDiff.y;
    let tLastNew = new Rectangle(
      new Point(left, top),
      tLast.width,
      tLast.height,
    );

    //expand
    let tBorder = this.canvasRect;
    let sz = this.state.daishiMeta.size;
    let splitCnt = this.state.daishiMeta.splitCnt;
    let fBorder = new Rectangle(
      new Point(0, 0),
      sz.cx * splitCnt.x,
      sz.cy * splitCnt.y,
    );
    let frames = this.expand(tLastNew, fLast, tBorder, fBorder);
    let toFrame = frames[0];
    let fromFrame = frames[1];

    //checkRange
    if (!this.checkRange(toFrame, fromFrame)) {
      this.state.daishiMeta.setTlast(toFrame);
      this.state.daishiMeta.setFlast(fromFrame);
    }

    //refresh
    this.refresh(toFrame, fromFrame);
  }

  public fixScrollTo(dragDiff: Point): void {
    //
    this.scrollTo(dragDiff);
  }

  /*
  refresh(toRNew:Rectangle,fromRNew:Rectangle):void
  {
    console.log(`refresh(to:${toRNew} (from:${fromRNew}))`);
    let  displayRect= toRNew;
    let  sourceRect= fromRNew;
    let  framess= this.state.daishiMeta.frameList(displayRect,sourceRect);

    //let  boredr= framess[0];
    let  frameLevelCellList= framess[1];
    let  images:ImageChunk[]= [];
    let  max= frameLevelCellList.length;
    if(max>1){
      this.state.daishiMeta.setTlast(toRNew);
      this.state.daishiMeta.setFlast(fromRNew);
      for(let i=0;i<max;++i){
        let  fLCL= frameLevelCellList[i];
        //local.ts resolveUrl(tag:TagID.frame:FRameLevelCellid)

        const
          tag= this.state.tag,
          level=fLCL.level,
          ll= level.toString(),
          cellid= fLCL.cellid,
          xx=cellid.x.toString(),
          yy=fLCL.cellid.y.toString(),
          dest= fLCL.toR,
          source= fLCL.fromR,
          filename= `cell.${ll}.${xx}.${yy}.jpg`,
          url= `/localdb/${tag}/${filename}`;
        //(4)****************************
        console.log(`url:${url} cellid:${cellid}`);
        images.push(new ImageChunk(url,source,dest));
      }//for(let i=0;i<max;++i)
      this.view.clearScreen(this.canvasRect);
      //this.view.showImages(images);
    }//if(max>1)
    //(5)****************************
    this.view.showImages(images);
    //(6)****************************
  }//refresh(
*/
  refresh(toRNew: Rectangle, fromRNew: Rectangle): void {
    console.log(`refresh(to:${toRNew} (from:${fromRNew}))`);
    let displayRect = toRNew;
    let sourceRect = fromRNew;
    let framess = this.state.daishiMeta.frameList(displayRect, sourceRect);
    let imageChunksPromise = framess[1].map((frame) =>
      this.repository.resolveUrl(this.state.tag, frame),
    );

    Promise.all(imageChunksPromise)
      .then((images) => {
        this.view.clearScreen(this.canvasRect);
        this.view.showImages(images);
      })
      .catch((reason) => console.error(reason));
  } //refresh(

  //Point固定Zooming:直前の表示状態でのrF <- rT投影で指定したpFの位置をピン止め
  zoomPoint(
    io: string,
    ratio: number,
    toP: Point,
    toR: Rectangle,
    fromR: Rectangle,
  ): Rectangle[] {
    if (io == "i") {
      //zoomIn
      let trWL = new TrWL(fromR, toR);
      ///CTrWL  trWL(toR, fromR);
      //let  fromP= trWL.getWorld_DP(toP);
      let fromP = trWL.getPointW(toP);
      ///DPoint  fromP = trWL.GetLocal_DP(toP);
      //(fromP.x - fromR.left)/(fromP.x - x1)==ratio
      let x1 = fromP.x - (fromP.x - fromR.left) / ratio;
      //(fromP.y - fromR.top)/(fromP.y - y1)==ratio
      let y1 = fromP.y - (fromP.y - fromR.top) / ratio;
      //(fromR.right - fromP.x)/(x2 - fromP.x)==ratio
      let x2 = fromP.x + (fromR.right - fromP.x) / ratio;
      //(fromR.bottom - fromP.y)/(y2 - fromP.y)==ratio
      let y2 = fromP.y + (fromR.bottom - fromP.y) / ratio;
      ///ASSERT((x1<x2) && (y1<y2));
      let fromRNew = new Rectangle(new Point(x1, y1), x2 - x1, y2 - y1);
      let toRNew = toR;
      let frames: Rectangle[] = [toRNew, fromRNew];
      return frames;
    } else {
      //(toP.x - toR.left)/(toP.x - x1)==ratio
      let x1 = toP.x - (toP.x - toR.left) / ratio;
      //(toP.y - toR.top)/(toP.y - y1)==ratio
      let y1 = toP.y - (toP.y - toR.top) / ratio;
      //(toR.right - toP.x)/(x2 - toP.x)==ratio
      let x2 = toP.x + (toR.right - toP.x) / ratio;
      //(toR.bottom - toP.y)/(y2 - toP.y)==ratio
      let y2 = toP.y + (toR.bottom - toP.y) / ratio;
      ///ASSERT((x1<x2) && (y1<y2));
      let toRNew = new Rectangle(new Point(x1, y1), x2 - x1, y2 - y1);
      let fromRNew = fromR;
      let frames: Rectangle[] = [toRNew, fromRNew];
      return frames;
    } //if (io == 'i')
  }

  //rS,rDをrSBorder,rDBorderの範囲内で縦横比を維持したまま拡大する
  expand(
    toR: Rectangle,
    fromR: Rectangle,
    toBorder: Rectangle,
    fromBorder: Rectangle,
  ): Rectangle[] {
    let trTF = new TrWL(fromR, toR);
    //fFrameClient
    let fFrameClient = trTF.getLocal(fromBorder);

    //viewDBのbfRectでClipping
    let fFrameClipping = fFrameClient.intersection(toBorder);
    let tFrameClipping = trTF.getWorld(fFrameClipping);

    let fromNew = tFrameClipping.intersection(fromBorder);
    let toNew = trTF.getLocal(fromNew);

    let frames: Rectangle[] = [toNew, fromNew];
    return frames;
  }

  //toRが１０ドット以下 or fRが１０ドット以下 -> false
  checkRange(tR: Rectangle, fR: Rectangle): boolean {
    if (tR.width < 10 || tR.height < 10) {
      return false;
    }
    if (fR.width < 10 || fR.height < 10) {
      return false;
    }
    //zoomでPixel等倍の３倍以上には、拡大しない
    //tR/fR > 2.0
    if (tR.width / fR.width > 3.0) {
      return false;
    }
    if (tR.height / fR.height > 3.0) {
      return false;
    }
    return true;
  }
} //class  Controller
